import axios from "axios";
import {PerformanceData} from "./PerformanceChartReducer";

// Consulta em lote da tela Performance (SGD-3903/SGD-3904).
// Sem barra inicial: GATEWAY_URL já termina com "/".
export const PERFORMANCE_BATCH_URL = "api/power-station/performance";

// RN08: até 4 tentativas por ciclo; o intervalo entre elas fica a critério da implementação.
export const PERFORMANCE_MAX_ATTEMPTS = 4;
export const PERFORMANCE_RETRY_DELAY_MS = 1000;

export const PERFORMANCE_ERROR_MESSAGE = "Erro ao carregar os dados de performance.";

/**
 * Spring espera powerStationIds repetido (powerStationIds=1&powerStationIds=2). Sem ids, a API
 * devolve todas as usinas do usuário (RF02).
 */
export const buildPerformanceParams = (
    startDateTime: string,
    endDateTime: string,
    powerStationIds?: number[]
): URLSearchParams => {
    const params = new URLSearchParams();
    params.append("startDateTime", startDateTime);
    params.append("endDateTime", endDateTime);
    (powerStationIds ?? []).forEach((id) => params.append("powerStationIds", String(id)));
    return params;
};

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Busca todas as usinas em uma requisição, tentando de novo até completar maxAttempts (RN08). */
export const fetchPerformanceBatch = async (
    startDateTime: string,
    endDateTime: string,
    powerStationIds: number[] | undefined,
    retryDelayMs: number = PERFORMANCE_RETRY_DELAY_MS,
    maxAttempts: number = PERFORMANCE_MAX_ATTEMPTS
): Promise<PerformanceData[]> => {
    const params = buildPerformanceParams(startDateTime, endDateTime, powerStationIds);
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const response = await axios.get(PERFORMANCE_BATCH_URL, {params});
            return response?.data?.data ?? [];
        } catch (error) {
            lastError = error;
            if (attempt < maxAttempts) {
                await wait(retryDelayMs);
            }
        }
    }
    throw lastError;
};

/**
 * Atualiza todos os cards de uma vez (RN05). Usina selecionada que não veio na resposta fica
 * "sem dados" (RN04), estado neutro e diferente do erro.
 */
export const applyPerformanceBatch = (
    cards: PerformanceData[],
    response: PerformanceData[]
): PerformanceData[] => {
    const responseById = new Map(response.map((item) => [item.id, item]));
    return cards.map((card) => {
        const data = responseById.get(card.id);
        if (data) {
            return {...data, loading: false, error: false, noData: false};
        }
        return {...card, loading: false, error: false, noData: true};
    });
};

/** Falha nas 4 tentativas: todos os cards passam a "dados não encontrados" (RN08). */
export const markPerformanceCardsAsError = (cards: PerformanceData[]): PerformanceData[] =>
    cards.map((card) => ({...card, loading: false, error: true, noData: false}));
